"use strict";
/**
 * Copyright 2023 actions-toolkit authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Inputs = void 0;
const fs_1 = __importDefault(require("fs"));
const path_1 = __importDefault(require("path"));
const core = __importStar(require("@actions/core"));
const sync_1 = require("csv-parse/sync");
const context_1 = require("../context");
class Inputs {
    getBuildImageIDFilePath() {
        return path_1.default.join(context_1.Context.tmpDir(), 'iidfile');
    }
    getBuildMetadataFilePath() {
        return path_1.default.join(context_1.Context.tmpDir(), 'metadata-file');
    }
    resolveBuildImageID() {
        const iidFile = this.getBuildImageIDFilePath();
        if (!fs_1.default.existsSync(iidFile)) {
            return undefined;
        }
        return fs_1.default.readFileSync(iidFile, { encoding: 'utf-8' }).trim();
    }
    resolveBuildMetadata() {
        const metadataFile = this.getBuildMetadataFilePath();
        if (!fs_1.default.existsSync(metadataFile)) {
            return undefined;
        }
        const content = fs_1.default.readFileSync(metadataFile, { encoding: 'utf-8' }).trim();
        if (content === 'null') {
            return undefined;
        }
        return content;
    }
    resolveDigest() {
        const metadata = this.resolveBuildMetadata();
        if (metadata === undefined) {
            return undefined;
        }
        const metadataJSON = JSON.parse(metadata);
        if (metadataJSON['containerimage.digest']) {
            return metadataJSON['containerimage.digest'];
        }
        return undefined;
    }
    resolveBuildSecretString(kvp) {
        return this.resolveBuildSecret(kvp, false);
    }
    resolveBuildSecretFile(kvp) {
        return this.resolveBuildSecret(kvp, true);
    }
    resolveBuildSecret(kvp, file) {
        const delimiterIndex = kvp.indexOf('=');
        const key = kvp.substring(0, delimiterIndex);
        let value = kvp.substring(delimiterIndex + 1);
        if (key.length == 0 || value.length == 0) {
            throw new Error(`${kvp} is not a valid secret`);
        }
        if (file) {
            if (!fs_1.default.existsSync(value)) {
                throw new Error(`secret file ${value} not found`);
            }
            value = fs_1.default.readFileSync(value, { encoding: 'utf-8' });
        }
        const secretFile = context_1.Context.tmpName({ tmpdir: context_1.Context.tmpDir() });
        fs_1.default.writeFileSync(secretFile, value);
        return `id=${key},src=${secretFile}`;
    }
    getProvenanceInput(name) {
        const input = core.getInput(name);
        if (!input) {
            // if input is not set returns empty string
            return input;
        }
        try {
            return core.getBooleanInput(name) ? `builder-id=${context_1.Context.provenanceBuilderID()}` : 'false';
        }
        catch (err) {
            // not a valid boolean, so we assume it's a string
            return this.resolveProvenanceAttrs(input);
        }
    }
    resolveProvenanceAttrs(input) {
        if (!input) {
            return `builder-id=${context_1.Context.provenanceBuilderID()}`;
        }
        // parse attributes from input
        const fields = (0, sync_1.parse)(input, {
            relaxColumnCount: true,
            skipEmptyLines: true
        })[0];
        // check if builder-id attribute exists in the input
        for (const field of fields) {
            const parts = field
                .toString()
                .split(/(?<=^[^=]+?)=/)
                .map(item => item.trim());
            if (parts[0] == 'builder-id') {
                return input;
            }
        }
        // if not add builder-id attribute
        return `${input},builder-id=${context_1.Context.provenanceBuilderID()}`;
    }
    static hasLocalExporter(exporters) {
        return Inputs.hasExporterType('local', exporters);
    }
    static hasTarExporter(exporters) {
        return Inputs.hasExporterType('tar', exporters);
    }
    static hasDockerExporter(exporters, load) {
        return load || Inputs.hasExporterType('docker', exporters);
    }
    static hasExporterType(name, exporters) {
        const records = (0, sync_1.parse)(exporters.join(`\n`), {
            delimiter: ',',
            trim: true,
            columns: false,
            relaxColumnCount: true
        });
        for (const record of records) {
            if (record.length == 1 && !record[0].startsWith('type=')) {
                // Local if no type is defined
                // https://github.com/docker/buildx/blob/d2bf42f8b4784d83fde17acb3ed84703ddc2156b/build/output.go#L29-L43
                return name == 'local';
            }
            for (const [key, value] of record.map(chunk => chunk.split('=').map(item => item.trim()))) {
                if (key == 'type' && value == name) {
                    return true;
                }
            }
        }
        return false;
    }
    static hasGitAuthTokenSecret(secrets) {
        for (const secret of secrets) {
            if (secret.startsWith('GIT_AUTH_TOKEN=')) {
                return true;
            }
        }
        return false;
    }
}
exports.Inputs = Inputs;
//# sourceMappingURL=inputs.js.map